import type {
  NumberLimitAction,
  NumberLimitChange,
  NumberLimitHistoryActors,
  NumberLimitHistoryEntry,
  NumberLimitHistoryQuery,
  NumberLimitValue,
} from '@nexbit/types'
import { BET_TYPE_LABEL } from './lottery-defaults'

/**
 * Turning a ประวัติการอั้นเลข entry into the sentence an operator came to read.
 *
 * Pure on purpose, and kept out of the two screens that render it: the question asked of
 * this table is always "ใครทำอะไรกับเลขนี้ เมื่อไหร่" and the answer is a string, which a
 * test can pin without a DOM. The screens lay it out; nothing here knows about React.
 */

/** What each action did, in the words used on the screens that perform it. */
export const ACTION_LABEL: Record<NumberLimitAction, string> = {
  'draw.number.block': 'ปิดรับเลข',
  'draw.number.unblock': 'เปิดรับเลขคืน',
  'draw.number_cap.set': 'ตั้งเพดานรายเลข',
  'draw.number_cap.delete': 'ลบเพดานรายเลข',
  'draw.limits.update': 'แก้วงเงินทั้งประเภท',
  'partner.number_rule.set': 'เอเย่นต์ตั้งกฎ',
  'partner.number_rule.delete': 'เอเย่นต์ลบกฎ',
  'partner.number_rule.clear': 'เอเย่นต์ล้างกฎทั้งหมด',
}

/**
 * Colour by what the change DID to the book, not by the verb.
 *
 * Closing a number or cutting a ceiling tightens the book; reopening or raising one
 * loosens it. They are opposite risks and reading the list at a glance has to separate
 * them — which is the same distinction the OTP gate on these screens is built around.
 */
export function actionVariant(
  action: NumberLimitAction,
): 'destructive' | 'success' | 'warning' | 'outline' {
  switch (action) {
    case 'draw.number.block':
      return 'destructive'
    case 'draw.number.unblock':
    case 'draw.number_cap.delete':
    case 'partner.number_rule.delete':
    case 'partner.number_rule.clear':
      return 'success'
    case 'draw.number_cap.set':
    case 'draw.limits.update':
    case 'partner.number_rule.set':
      return 'warning'
  }
}

/** Thousands separators, and no decimals on a round figure. 20000.00000000 → 20,000 */
export function money(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return String(v)
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
}

export function betTypeLabel(betType: string): string {
  return BET_TYPE_LABEL[betType as keyof typeof BET_TYPE_LABEL] ?? betType
}

/** '123' · 'สูง' · 'ทั้งประเภท' when the change was about a whole bet type. */
export function numberLabel(number: string | null): string {
  if (number === null) return 'ทั้งประเภท'
  switch (number) {
    case 'high': return 'สูง'
    case 'low': return 'ต่ำ'
    case 'odd': return 'คี่'
    case 'even': return 'คู่'
    default: return number
  }
}

/**
 * One side of a change, as a short phrase.
 *
 * null is 'ตามค่าปกติ' rather than blank or a dash: before a rule existed, and after one
 * is removed, the number is not unlimited — it is on its bet type's own figure, and
 * "blank" would read as though nothing applied.
 */
export function valueLabel(v: NumberLimitValue | null): string {
  if (v === null) return 'ตามค่าปกติ'
  const parts: string[] = []
  if (v.blocked) parts.push('ปิดรับ')
  if (v.maxPerNumber != null) parts.push(`เพดาน ${money(v.maxPerNumber)}`)
  if (v.maxBet != null) parts.push(`ต่อบิล ${money(v.maxBet)}`)
  if (v.maxPerType != null) parts.push(`ต่อประเภท ${money(v.maxPerType)}`)
  if (v.payoutRate != null) parts.push(`จ่าย ${money(v.payoutRate)}`)
  return parts.length > 0 ? parts.join(' · ') : 'ตามค่าปกติ'
}

/**
 * The whole change in one line: 'เพดาน 1,000 → เพดาน 20,000'.
 *
 * Both sides always, even when one is 'ตามค่าปกติ'. A history that printed only the new
 * value would answer "what is it now" — which the ปิดรับเลข screen already shows. The
 * question only this screen can answer is what it was before.
 */
export function changeLabel(c: NumberLimitChange): string {
  return `${valueLabel(c.before)} → ${valueLabel(c.after)}`
}

/**
 * Whether anything actually moved.
 *
 * A save that re-states a rule unchanged is a real entry — somebody pressed บันทึก — but
 * it is not a change, and marking it lets the screen grey it out instead of showing
 * 'เพดาน 500 → เพดาน 500' as though something had happened.
 */
export function isNoOp(c: NumberLimitChange): boolean {
  return valueLabel(c.before) === valueLabel(c.after)
}

/** 'ทุกงวด' or 'งวดนี้เท่านั้น' — the difference between forever and tonight. */
export function scopeLabel(entry: NumberLimitHistoryEntry): string {
  return entry.drawId === null ? 'ทุกงวด' : 'งวดนี้เท่านั้น'
}

/** Who the change applied to: everyone, or one agent's own customers. */
export function whoseBook(entry: NumberLimitHistoryEntry): string {
  if (entry.scope === 'house') return 'ทั้งเว็บ'
  return entry.partnerCode ? `เอเย่นต์ ${entry.partnerCode}` : 'เอเย่นต์'
}

/**
 * A one-line summary for the collapsed row.
 *
 * Caps the number of targets listed. An admin pasting thirty numbers into one save is a
 * single entry, and thirty chips in a table cell is a wall — the rest are counted and the
 * detail panel has them all.
 */
export const SUMMARY_LIMIT = 3

export function summarize(entry: NumberLimitHistoryEntry): {
  shown: NumberLimitChange[]
  more: number
  /** Set when the entry has no readable targets at all. */
  fallback: string | null
} {
  if (entry.clearedCount !== null) {
    return { shown: [], more: 0, fallback: `ลบกฎ ${entry.clearedCount} รายการ` }
  }
  if (entry.changes.length === 0) {
    // Entries written before the delete paths recorded the row they removed. There is
    // nothing to show, and saying so beats an empty cell that reads as a rendering bug.
    return { shown: [], more: 0, fallback: 'ไม่มีรายละเอียดบันทึกไว้' }
  }
  return {
    shown: entry.changes.slice(0, SUMMARY_LIMIT),
    more: Math.max(0, entry.changes.length - SUMMARY_LIMIT),
    fallback: null,
  }
}

// ── The "ตั้งโดย" picker ───────────────────────────────────────────────────────

/**
 * One control for "ใครตั้ง", because the question is asked at two depths and splitting it
 * into two controls would make "เอเย่นต์ทั้งหมด" and "เอเย่นต์ agent01" look like separate
 * axes that can be combined. They cannot: the second is a narrowing of the first.
 *
 *   ''              ทุกคน
 *   'house'         any admin
 *   'agent'         any agent
 *   'actor:<id>'    one admin account
 *   'agent:<id>'    one agent's book
 */
export function parseSetBy(
  value: string,
): Pick<NumberLimitHistoryQuery, 'scope' | 'partnerId' | 'actorId'> {
  if (value === 'house' || value === 'agent') return { scope: value }
  const [kind, id] = value.split(':')
  if (!id) return {}
  // partnerId already implies the agent actions in the query, so the scope is not
  // restated — two filters saying the same thing is two places to get it wrong.
  if (kind === 'agent') return { partnerId: id }
  if (kind === 'actor') return { actorId: id }
  return {}
}

/**
 * The picker's named accounts, grouped by side.
 *
 * Only the individual accounts. "แอดมินทุกคน" and "เอเย่นต์ทุกราย" are offered at the top
 * level by the screen itself and not through here, so that filtering by side keeps
 * working while this list is still loading — or if it never arrives.
 *
 * A side nobody appears on is left out rather than given an empty heading.
 */
export function setByOptions(actors: NumberLimitHistoryActors | null): {
  label: string
  options: { value: string; label: string }[]
}[] {
  if (!actors) return []
  const groups = [{
    label: 'เจาะจงแอดมิน',
    options: actors.admins.map((a) => ({ value: `actor:${a.id}`, label: a.username })),
  }, {
    label: 'เจาะจงเอเย่นต์',
    options: actors.agents.map((a) => ({
      value: `agent:${a.id}`,
      // The code is what the console shows everywhere else; the name is added only when
      // it says something the code does not.
      label: a.name && a.name !== a.code ? `${a.code} — ${a.name}` : a.code,
    })),
  }]
  return groups.filter((g) => g.options.length > 0)
}

/**
 * A yyyy-MM-dd date input as an ICT day boundary in ISO.
 *
 * The operators are in Bangkok and the server compares tz-aware instants, so a bare date
 * would silently mean UTC midnight — seven hours of the previous Thai day inside the
 * window, and seven hours of the last day outside it.
 */
export function ictStart(date: string): string {
  return new Date(`${date}T00:00:00+07:00`).toISOString()
}

/** The exclusive end of a day range, so picking one date covers that whole day. */
export function ictEndExclusive(date: string): string {
  const d = new Date(`${date}T00:00:00+07:00`)
  d.setDate(d.getDate() + 1)
  return d.toISOString()
}

/** Today in Bangkok as yyyy-MM-dd — the date the screen opens on. */
export function todayIct(): string {
  const now = new Date()
  const ict = new Date(now.getTime() + (7 * 60 + now.getTimezoneOffset()) * 60_000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${ict.getFullYear()}-${p(ict.getMonth() + 1)}-${p(ict.getDate())}`
}

/** N days before today in Bangkok, for the quick range buttons. */
export function ictDaysAgo(days: number): string {
  const d = new Date(`${todayIct()}T00:00:00+07:00`)
  d.setDate(d.getDate() - days)
  const p = (n: number) => String(n).padStart(2, '0')
  // Read back in ICT, not local parts, so the arithmetic does not slide across midnight.
  const ict = new Date(d.getTime() + 7 * 3_600_000)
  return `${ict.getUTCFullYear()}-${p(ict.getUTCMonth() + 1)}-${p(ict.getUTCDate())}`
}
